<template>
  <div class="pa-page" :class="{ 'pa-page--customer': channel === 'customer' }">
      <header class="pa-header">
        <div class="pa-header-left">
          <button type="button" class="back-btn" @click="goBack">
            <v-icon size="18" color="#1E4276">mdi-arrow-left</v-icon>
          </button>
          <div>
            <p class="breadcrumb">{{ paConfig.labels.saleBreadcrumb }}</p>
            <h1 class="title">{{ paConfig.labels.saleTitle }}</h1>
          </div>
        </div>
        <button type="button" class="btn-new-sale" @click="showNewSaleConfirm = true">
          <v-icon x-small color="#1E4276" class="tw-mr-1">mdi-refresh</v-icon>
          {{ paConfig.channel === 'customer' ? 'Start over' : 'New Sale' }}
        </button>
      </header>

      <referral-agent-banner v-if="channel === 'customer'" />

      <v-dialog v-model="showNewSaleConfirm" max-width="380" overlay-opacity="0.5">
        <div class="new-sale-dialog">
          <div class="nsd-header">
            <div class="nsd-icon">
              <v-icon size="24" color="white">mdi-alert-circle-outline</v-icon>
            </div>
            <div>
              <p class="nsd-title">Start a New Sale?</p>
              <p class="nsd-sub">{{ paConfig.labels.newSaleSub }}</p>
            </div>
          </div>
          <div class="nsd-body">
            <p class="nsd-desc">Unsaved details in this session will be removed. Existing saved quotes remain in the system.</p>
          </div>
          <div class="nsd-footer">
            <button class="nsd-btn-cancel" @click="showNewSaleConfirm = false">Keep Working</button>
            <button class="nsd-btn-confirm" @click="startNewSale">
              <v-icon x-small color="white" class="tw-mr-1">mdi-refresh</v-icon>
              Yes, Start New
            </button>
          </div>
        </div>
      </v-dialog>

      <div class="step-progress">
        <div
          v-for="(label, i) in steps"
          :key="label"
          class="step-item"
          :class="{ active: activeStep === i, done: activeStep > i }"
        >
          <span class="step-dot">{{ activeStep > i ? '✓' : i + 1 }}</span>
          <span class="step-label">{{ label }}</span>
        </div>
      </div>

      <div
        class="quote-layout"
        :class="{ 'quote-layout--checkout': useCognitoIdentity && activeStep === 3 }"
      >
        <div class="quote-form-column">
          <!-- Step 0: IPRS identity verification + KRA auto-fetch (same as motor) -->
          <div v-show="activeStep === 0" class="section-card">
            <div class="section-header">
              <span class="section-number">1</span>
              <h3>{{ paConfig.labels.idSection }}</h3>
            </div>
            <div class="pa-form-fields">
              <div class="cover-start-block">
                <label class="input-label">Cover start date <span class="req">*</span></label>
                <v-text-field
                  v-model="coverStartDate"
                  type="date"
                  :min="coverStartMin"
                  :max="coverStartMax"
                  outlined
                  dense
                  hide-details="auto"
                  class="custom-input cover-start-input"
                  @change="clampCoverStartDate"
                />
              </div>

              <div class="section-divider" />

              <p class="subsection-title">Customer identification</p>
              <p v-if="paConfig.labels.customerIdHint" class="step-hint step-hint--subsection">
                {{ paConfig.labels.customerIdHint }}
              </p>

              <!-- Customer: account details from Cognito — auto-verified on load -->
              <template v-if="useCognitoIdentity && !idVerified">
                <div v-if="!cognitoIdReady" class="alert-error">
                  Your Pacis account is missing a valid National ID. Update your profile or contact support before continuing.
                </div>
                <template v-else>
                  <div class="account-id-card">
                    <p class="account-id-label">From your Pacis account</p>
                    <div class="account-id-row">
                      <span class="account-id-key">National ID</span>
                      <span class="account-id-val">{{ nationalId }}</span>
                    </div>
                    <div v-if="accountDisplayName" class="account-id-row">
                      <span class="account-id-key">Name</span>
                      <span class="account-id-val">{{ accountDisplayName }}</span>
                    </div>
                  </div>
                  <div v-if="fetching" class="verify-auto-status tw-mt-3">
                    <v-progress-circular indeterminate size="18" width="2" color="primary" />
                    <span>Confirming your identity…</span>
                  </div>
                  <div v-if="harvestMismatch" class="alert-error tw-mt-3">
                    We could not match your account name with IPRS for ID {{ nationalId }}.
                    Contact support if your profile details are incorrect.
                  </div>
                </template>
              </template>

              <!-- Agent: manual ID + first name entry -->
              <template v-else-if="!idVerified">
                <label class="input-label">Expected First Name <span class="req">*</span></label>
                <v-text-field
                  v-model="expectedFirstName"
                  placeholder="e.g. JOHN"
                  outlined dense hide-details="auto" maxlength="50"
                  class="custom-input tw-uppercase"
                  autocomplete="off"
                  @keyup.enter="verifyIdentity"
                />

                <label class="input-label tw-mt-3">National ID Number <span class="req">*</span></label>
                <div class="verify-row">
                  <v-text-field
                    v-model="nationalId"
                    placeholder="e.g. 12345678"
                    type="number" outlined dense hide-details="auto"
                    class="custom-input tw-flex-1"
                    autocomplete="off"
                    maxlength="9"
                    inputmode="numeric"
                    @keyup.enter="verifyIdentity"
                    @blur="nationalId = (nationalId || '').replace(/\D/g, '').slice(0, 9)"
                  />
                  <button
                    type="button" class="verify-btn"
                    :disabled="fetching || !nationalId || !expectedFirstName"
                    @click="verifyIdentity"
                  >
                    <v-progress-circular v-if="fetching" indeterminate size="16" width="2" color="white" />
                    <template v-else><v-icon small color="white">mdi-magnify</v-icon> Verify</template>
                  </button>
                </div>

                <div v-if="harvestMismatch" class="alert-error tw-mt-3">
                  The first name "<strong>{{ expectedFirstName }}</strong>" does not match IPRS records
                  for ID {{ nationalId }}. Please check the name and retry.
                </div>
              </template>

              <!-- Verified identity summary -->
              <div v-if="idVerified" class="iprs-card">
                <div v-if="!useCognitoIdentity" class="iprs-card-actions">
                  <button type="button" class="reset-link" @click="resetIdentity">
                    Change
                  </button>
                </div>
                <div class="verified-details">
                  <div
                    v-for="row in verifiedDetailRows"
                    :key="row.label"
                    class="verified-detail-row"
                  >
                    <span class="verified-detail-key">{{ row.label }}</span>
                    <span class="verified-detail-val">{{ row.value }}</span>
                  </div>
                </div>
              </div>

              <!-- Manual fallback when IPRS can't verify -->
              <div v-if="iprsUnavailable && !idVerified" class="manual-block tw-mt-3">
                <div class="alert-info">
                  {{ manualIdHint }}
                </div>
                <label class="input-label tw-mt-3">Full Name <span class="req">*</span></label>
                <v-text-field
                  v-model="proposerName"
                  placeholder="Full name as per ID"
                  outlined dense hide-details="auto"
                  class="custom-input"
                  autocomplete="off"
                />
              </div>

              <div v-if="(idVerified || iprsUnavailable) && !kraVerified" class="kra-manual-block tw-mt-3">
                <p class="field-hint field-hint--warn">
                  Tax registration could not be verified automatically. Enter the KRA PIN manually to continue.
                </p>
                <label class="input-label tw-mt-3">KRA PIN <span class="req">*</span></label>
                <v-text-field
                  v-model="kraPin"
                  placeholder="e.g. A012345678Z"
                  outlined
                  dense
                  hide-details="auto"
                  maxlength="11"
                  class="custom-input tw-uppercase"
                  autocomplete="off"
                  @blur="kraPin = sanitizeKra(kraPin)"
                />
                <p v-if="kraPin && !kraValid" class="field-hint field-hint--warn tw-mt-1">
                  Enter a valid KRA PIN (e.g. A012345678Z)
                </p>
              </div>

              <!-- Remaining details not provided by IPRS -->
              <div v-if="idVerified || iprsUnavailable" class="proposer-extra tw-mt-4">
                <p class="subsection-title">Student &amp; contact details</p>
                <pa-proposer-form
                  ref="proposerForm"
                  :form="proposer"
                  :lock-contact-fields="useCognitoIdentity"
                  :reject-agent-contact="channel === 'agent'"
                  @update:form="proposer = $event"
                />
              </div>
            </div>
            <div class="actions-row">
              <button type="button" class="btn-primary" :disabled="!idCanProceed" @click="confirmIdAndNext">
                Next
                <v-icon small color="white" class="tw-ml-1">mdi-arrow-right</v-icon>
              </button>
            </div>
          </div>

          <div v-show="activeStep === 1" class="section-card">
            <div class="section-header">
              <span class="section-number">2</span>
              <h3>Health declaration</h3>
            </div>
            <p class="step-hint">Share any relevant health details for underwriting.</p>
            <pa-health-declaration :health="health" @update:health="health = $event" />
            <div class="actions-row">
              <button type="button" class="btn-outline" @click="activeStep = 0">Back</button>
              <button type="button" class="btn-primary" @click="activeStep = 2">Next</button>
            </div>
          </div>

          <div v-show="activeStep === 2" class="section-card">
            <div class="section-header">
              <span class="section-number">3</span>
              <h3>Next of kin</h3>
            </div>
            <p class="step-hint">Provide the next of kin details for this cover.</p>
            <pa-next-of-kin-form ref="kinForm" :form="kin" @update:form="kin = $event" />
            <div class="actions-row">
              <button type="button" class="btn-outline" @click="activeStep = 1">Back</button>
              <button type="button" class="btn-primary" @click="goToPayment">Next</button>
            </div>
          </div>

          <div v-show="activeStep === 3" class="section-card section-card--payment">
            <div class="section-header">
              <span class="section-number">4</span>
              <h3>{{ useCognitoIdentity ? 'Payment' : 'Review & pay' }}</h3>
            </div>

            <!-- Customer: cover summary + M-Pesa STK / Paybill (motor agent pattern) -->
            <template v-if="useCognitoIdentity">
              <div class="pay-summary-card tw-mb-5">
                <div class="pay-summary-head">
                  <div class="pay-summary-head-icon">
                    <v-icon x-small color="white">mdi-shield-check</v-icon>
                  </div>
                  <span>Your cover</span>
                </div>
                <div class="pay-summary-section">
                  <p class="pay-sec-label">Plan</p>
                  <div class="pay-sum-row">
                    <span class="pay-sum-label">Cover option</span>
                    <span class="pay-sum-value">{{ quote.optionName || '—' }}</span>
                  </div>
                  <div class="pay-sum-row">
                    <span class="pay-sum-label">Duration</span>
                    <span class="pay-sum-value">{{ durationLabel || '—' }}</span>
                  </div>
                  <div class="pay-sum-row">
                    <span class="pay-sum-label">Cover period</span>
                    <span class="pay-sum-value">{{ coverPeriodLabel || '—' }}</span>
                  </div>
                  <div v-if="effectiveQuoteId" class="pay-sum-row">
                    <span class="pay-sum-label">Quote ref.</span>
                    <span class="pay-sum-value">{{ effectiveQuoteId }}</span>
                  </div>
                </div>
                <div class="pay-premium-footer">
                  <div class="pay-prem-total">
                    <span>Amount due now</span>
                    <span class="pay-prem-total-val">{{ formattedPremium }}</span>
                  </div>
                </div>
              </div>

              <v-checkbox v-model="declarationsAccepted" hide-details class="pay-decl-check tw-mb-4">
                <template #label>
                  <span class="decl-text">{{ declarationLabel }}</span>
                </template>
              </v-checkbox>

              <pa-payment-methods
                :paying="paying"
                :declarations-accepted="declarationsAccepted"
                :national-id-ready="!!quote.nationalId"
                :kra-ready="!!quote.kraPin"
                :payment-reference="paymentReference"
                :formatted-premium="formattedPremium"
                :mpesa-phone.sync="mpesaPhone"
                :active-pay-tab.sync="activePayTab"
                :paybill-fallback="paybillFallback"
                :ussd-steps="ussdSteps"
                @pay-mpesa="pay('mpesa')"
                @verify-paybill="verifyPaybillPayment"
              />
              <p v-if="mockEnabled" class="demo-note">Demo mode — no real charge.</p>
              <div class="actions-row tw-mt-4">
                <button type="button" class="btn-outline" @click="activeStep = 2">Back</button>
              </div>
            </template>

            <!-- Agent: full review card -->
            <template v-else>
              <div class="review-block">
                <div class="review-head">
                  <p class="review-name">{{ reviewDisplayName }}</p>
                  <span class="review-status">Ready for payment</span>
                </div>
                <div class="review-grid">
                  <div class="review-item">
                    <span class="review-key">National ID</span>
                    <span class="review-val">{{ quote.nationalId }}</span>
                  </div>
                  <div v-if="quote.kraPin" class="review-item">
                    <span class="review-key">KRA PIN</span>
                    <span class="review-val">{{ quote.kraPin }}</span>
                  </div>
                  <div class="review-item">
                    <span class="review-key">Cover</span>
                    <span class="review-val">{{ quote.optionName }} · {{ durationLabel }}</span>
                  </div>
                  <div class="review-item">
                    <span class="review-key">Cover period</span>
                    <span class="review-val">{{ coverPeriodLabel || '—' }}</span>
                  </div>
                </div>
                <div class="premium-pill">
                  <span class="premium-key">Premium</span>
                  <span class="premium-val">KES {{ formatMoney(quote.premium) }}</span>
                </div>
              </div>
              <v-checkbox v-model="declarationsAccepted" hide-details class="review-check tw-mt-4">
                <template #label>
                  <span class="decl-text">{{ declarationLabel }}</span>
                </template>
              </v-checkbox>

              <pa-payment-methods
                class="tw-mt-4"
                :paying="paying"
                :declarations-accepted="declarationsAccepted"
                :national-id-ready="!!quote.nationalId"
                :kra-ready="!!quote.kraPin"
                :payment-reference="paymentReference"
                :formatted-premium="formattedPremium"
                :mpesa-phone.sync="mpesaPhone"
                :active-pay-tab.sync="activePayTab"
                :paybill-fallback="paybillFallback"
                :ussd-steps="ussdSteps"
                @pay-mpesa="pay('mpesa')"
                @verify-paybill="verifyPaybillPayment"
              />
              <p v-if="mockEnabled" class="demo-note">Mock mode — no real charge.</p>
              <div class="actions-row tw-mt-4">
                <button type="button" class="btn-outline" @click="activeStep = 2">Back</button>
              </div>
            </template>
          </div>
        </div>

        <aside v-if="!(useCognitoIdentity && activeStep === 3)" class="quote-sidebar">
          <pa-quote-sidebar
            :option-name="quote.optionName"
            :duration-label="durationLabel"
            :premium="quote.premium"
            :quote="quote"
            :mock-enabled="mockEnabled"
            :checkout-mode="useCognitoIdentity && activeStep === 3"
            :cover-start-date="coverStartDate"
            :cover-end-date="coverEndDate"
            continue-label=""
          />
        </aside>
      </div>
  </div>
</template>

<script>
import PaProposerForm from '@/components/agent/pa/PaProposerForm';
import PaHealthDeclaration from '@/components/agent/pa/PaHealthDeclaration';
import PaNextOfKinForm from '@/components/agent/pa/PaNextOfKinForm';
import PaQuoteSidebar from '@/components/agent/pa/PaQuoteSidebar';
import PaPaymentMethods from '@/components/pa/PaPaymentMethods';
import ReferralAgentBanner from '@/components/shared/ReferralAgentBanner';
import { PA_COURSE_DURATIONS } from '@/constants/paStudentsRateCard';
import { buildPaPaymentReference } from '@/utils/paPaymentReference';
import {
  getPaQuotation,
  recalculatePaPremium,
  updatePaQuotation,
  initPaPolicy,
  requestPaStkPush,
  pollPaPaymentStatus,
  confirmPaPaybillPayment,
  issuePaPolicy,
  verifyPaIdentity,
  isAgentPaMockEnabled,
  resolveAgentNumericId,
} from '@/services/agentPaApi';
import { referralAgencyNumericId } from '@/services/referralApi';
import {
  loadPaPendingQuote,
  savePaPendingQuote,
  savePaSaleProgress,
  loadPaSaleProgress,
  clearPaQuoteCache,
  clearAllSaleData,
} from '@/utils/saleCache';
import { validateNationalId, validateKraPin, normalizeKenyaMobile } from '@/utils/paValidation';
import { agentContactError } from '@/utils/agentSelfContact';
import { getPaChannelConfig, getCognitoIdentityProfile } from '@/utils/paChannel';
import { invalidateAfterPayment } from '@/utils/portalBootstrap';
import {
  computePaCoverEndDate,
  formatPaCoverDateDisplay,
  getDefaultPaCoverStartDate,
  getPaCoverStartMaxDate,
  getPaCoverStartMinDate,
  isPaCoverStartDateValid,
} from '@/utils/paCoverDates';
import { mapGetters } from 'vuex';
import { trackAgentInteraction } from '@/utils/agentAnalytics';

export default {
  name: 'PaSalePage',
  components: {
    PaProposerForm,
    PaHealthDeclaration,
    PaNextOfKinForm,
    PaQuoteSidebar,
    PaPaymentMethods,
    ReferralAgentBanner,
  },
  props: {
    channel: {
      type: String,
      required: true,
      validator: (v) => v === 'agent' || v === 'customer',
    },
  },
  data() {
    return {
      activeStep: 0,
      quote: {},
      expectedFirstName: '',
      nationalId: '',
      kraPin: '',
      proposerName: '',
      gender: '',
      dateOfBirth: '',
      occupation: '',
      proposer: {
        studentId: '',
        universityName: '',
        phone: '',
        email: '',
        occupation: 'Student on attachment',
      },
      fetching: false,
      customerAutoVerifyStarted: false,
      idVerified: false,
      kraVerified: false,
      kraTaxpayerName: '',
      iprsUnavailable: false,
      harvestMismatch: false,
      showNewSaleConfirm: false,
      health: {
        goodHealth: 'yes',
        goodHealthDetails: '',
        medicalConditions: 'no',
        medicalConditionsDetails: '',
        accidentHistory: '',
      },
      kin: {
        kinName: '',
        kinId: '',
        kinContact: '',
        kinRelation: '',
      },
      declarationsAccepted: false,
      paying: false,
      mpesaPhone: '',
      activePayTab: 'mpesa',
      paybillFallback: false,
      pollTimer: null,
      agentPaymentId: 0,
      coverStartDate: getDefaultPaCoverStartDate(),
    };
  },
  beforeDestroy() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    paConfig() {
      return getPaChannelConfig({ paChannel: this.channel });
    },
    declarationLabel() {
      return this.paConfig.channel === 'customer'
        ? 'I confirm the information is accurate and I accept the terms.'
        : 'I confirm the information is accurate and the client accepts the terms.';
    },
    manualIdHint() {
      return this.paConfig.channel === 'customer'
        ? 'IPRS could not verify this ID right now. Enter your details manually to continue.'
        : 'IPRS could not verify this ID right now. Enter the customer\'s details manually to continue.';
    },
    reviewDisplayName() {
      if (this.paConfig.hideVerifiedName) return 'Your application';
      return this.quote.proposerName || '—';
    },
    useCognitoIdentity() {
      return this.channel === 'customer';
    },
    cognitoProfile() {
      return this.useCognitoIdentity
        ? getCognitoIdentityProfile(this.authenticatedUser)
        : null;
    },
    cognitoIdReady() {
      return validateNationalId(this.nationalId).valid;
    },
    accountDisplayName() {
      return this.cognitoProfile?.fullName || this.proposerName || '';
    },
    formattedPremium() {
      return `KES ${this.formatMoney(this.quote.premium)}`;
    },
    ussdSteps() {
      return [
        'Go to <strong>M-Pesa</strong> — open the <strong>M-Pesa App / SIM Toolkit</strong>, or dial <strong>*334#</strong>',
        'Select <strong>Lipa Na M-Pesa</strong> → <strong>Pay Bill</strong>',
        'Enter Business No. <strong>504700</strong>',
        `Enter Account No. <strong>${this.paymentReference || '—'}</strong>`,
        `Enter amount <strong>${this.formattedPremium}</strong>`,
        'Enter your <strong>M-Pesa PIN</strong> and confirm',
        'You will receive an <strong>M-Pesa confirmation SMS</strong> — then tap <strong>Validate payment</strong>',
      ];
    },
    verifiedDetailRows() {
      const rows = [];
      const name = (this.proposerName || '').trim();
      if (name) rows.push({ label: 'Full name', value: name });
      if (this.nationalId) rows.push({ label: 'National ID', value: this.nationalId });
      if (this.kraPin && this.kraValid) {
        rows.push({ label: 'KRA PIN', value: this.kraPin });
      }
      return rows;
    },
    steps() {
      return this.paConfig.saleSteps;
    },
    effectiveQuoteId() {
      return this.$route.query.quoteId || this.quote.quoteId;
    },
    durationLabel() {
      const d = PA_COURSE_DURATIONS.find((x) => x.code === this.quote.courseDuration);
      return d?.label || '';
    },
    coverStartMin() {
      return getPaCoverStartMinDate();
    },
    coverStartMax() {
      return getPaCoverStartMaxDate();
    },
    coverEndDate() {
      return computePaCoverEndDate(this.coverStartDate, this.quote.courseDuration);
    },
    coverStartDisplay() {
      return formatPaCoverDateDisplay(this.coverStartDate);
    },
    coverEndDisplay() {
      return formatPaCoverDateDisplay(this.coverEndDate);
    },
    coverPeriodLabel() {
      if (!this.coverStartDate) return '';
      const start = this.coverStartDisplay;
      const end = this.coverEndDisplay;
      return end ? `${start} → ${end}` : start;
    },
    mockEnabled() {
      return isAgentPaMockEnabled();
    },
    kraValid() {
      return validateKraPin(this.kraPin).valid;
    },
    idCanProceed() {
      const idOk = validateNationalId(this.nationalId).valid;
      if (!idOk || !this.kraValid) return false;
      const coverOk = isPaCoverStartDateValid(this.coverStartDate);
      if (this.idVerified) return coverOk;
      if (this.iprsUnavailable) return !!(this.proposerName || '').trim() && coverOk;
      return false;
    },
    paymentReference() {
      return buildPaPaymentReference(this.quote.nationalId || this.nationalId);
    },
  },
  async mounted() {
    const email = this.authenticatedUser?.email;
    if (this.channel === 'agent') {
      this.agentPaymentId = await resolveAgentNumericId(this.authenticatedUser);
    } else {
      await this.$store.dispatch('referral/ensureReferralValidated', this.$route.query);
      this.agentPaymentId = referralAgencyNumericId(this.$store.getters['referral/referralAgent']);
    }
    let q = loadPaPendingQuote(email, { liveOnly: !this.mockEnabled }) || {};
    const routeQuoteId = this.$route.query.quoteId;
    const liveRouteId = !this.mockEnabled ? /^\d+$/.test(String(routeQuoteId || '')) : true;
    if (routeQuoteId && liveRouteId) {
      const res = await getPaQuotation(routeQuoteId, this.channel);
      if (res.success) {
        q = { ...q, ...res.data };
        if (q.optionCode && q.courseDuration) {
          const recalc = await recalculatePaPremium(q.optionCode, q.courseDuration, q.quoteId);
          if (recalc.success && recalc.data?.premium != null) {
            q = { ...q, premium: recalc.data.premium };
          }
        }
      }
    }
    if (!q?.quoteId) {
      this.$router.replace({ name: this.paConfig.routes.planSelect });
      return;
    }
    this.quote = q;
    if (q.coverFrom && isPaCoverStartDateValid(q.coverFrom)) {
      this.coverStartDate = q.coverFrom;
    }
    this.mpesaPhone = normalizeKenyaMobile(q.phone || q.mpesaPhone || '');
    this.nationalId = q.nationalId || '';
    this.kraPin = q.kraPin || '';
    this.proposerName = q.proposerName || '';
    this.proposer = {
      studentId: q.studentId || '',
      universityName: q.universityName || '',
      phone: normalizeKenyaMobile(q.phone || ''),
      email: q.email || '',
      occupation: q.occupation || 'Student on attachment',
    };

    const progress = loadPaSaleProgress(email, this.effectiveQuoteId);
    if (progress) {
      this.health = { ...this.health, ...progress.health };
      this.kin = { ...this.kin, ...progress.kin };
      if (progress.nationalId) this.nationalId = progress.nationalId;
      if (progress.kraPin) this.kraPin = progress.kraPin;
      if (progress.proposerName) this.proposerName = progress.proposerName;
      if (progress.expectedFirstName) this.expectedFirstName = progress.expectedFirstName;
      if (progress.proposer) this.proposer = { ...this.proposer, ...progress.proposer };
      if (progress.coverStartDate && isPaCoverStartDateValid(progress.coverStartDate)) {
        this.coverStartDate = progress.coverStartDate;
      }
      this.kraVerified = !!progress.kraVerified;
      this.iprsUnavailable = !!progress.iprsUnavailable;
    }

    const proposerPhone = normalizeKenyaMobile(this.proposer.phone);
    if (proposerPhone) {
      this.proposer = { ...this.proposer, phone: proposerPhone };
    }
    this.mpesaPhone = normalizeKenyaMobile(this.mpesaPhone) || this.mpesaPhone;

    // Treat an already-captured ID + KRA + name as previously verified.
    const alreadyCaptured = validateNationalId(this.nationalId).valid
      && validateKraPin(this.kraPin).valid
      && !!(this.proposerName || '').trim();
    if (alreadyCaptured) {
      this.idVerified = true;
    }

    if (this.useCognitoIdentity) {
      this.applyCustomerCognitoProfile();
      await this.autoVerifyCustomerIdentity();
    }

    if (progress && progress.activeStep != null) {
      this.activeStep = progress.activeStep;
    }
    if (this.useCognitoIdentity && this.activeStep > 0 && !this.isDetailsStepComplete()) {
      this.activeStep = 0;
    }
    if (this.channel === 'agent' && this.activeStep > 0
      && agentContactError(this.proposer || {}, this.authenticatedUser)) {
      this.activeStep = 0;
    }
  },
  methods: {
    isDetailsStepComplete() {
      if (!isPaCoverStartDateValid(this.coverStartDate)) return false;
      if (!validateNationalId(this.nationalId).valid || !validateKraPin(this.kraPin).valid) return false;
      if (!this.idVerified && !this.iprsUnavailable) return false;
      if (this.iprsUnavailable && !(this.proposerName || '').trim()) return false;
      const p = this.proposer || {};
      return !!(p.studentId || '').trim()
        && !!(p.universityName || '').trim()
        && !!(p.phone || '').trim()
        && !!(p.email || '').trim()
        && !!(p.occupation || '').trim();
    },
    async autoVerifyCustomerIdentity() {
      if (this.customerAutoVerifyStarted || this.idVerified || !this.cognitoIdReady) {
        return;
      }
      this.customerAutoVerifyStarted = true;
      await this.verifyIdentity();
    },
    applyCustomerCognitoProfile() {
      const profile = getCognitoIdentityProfile(this.authenticatedUser);
      if (!profile) return;
      if (!this.nationalId && profile.idNumber) {
        this.nationalId = profile.idNumber;
      }
      if (!this.proposerName && profile.fullName) {
        this.proposerName = profile.fullName;
      }
      if (!this.expectedFirstName && profile.firstName) {
        this.expectedFirstName = profile.firstName;
      }
      if (!this.kraPin && profile.kraPin) {
        const kraCheck = validateKraPin(profile.kraPin);
        if (kraCheck.valid) {
          this.kraPin = kraCheck.normalized;
        }
      }
      if (!this.proposer.email && profile.email) {
        this.proposer = { ...this.proposer, email: profile.email };
      }
      const profilePhone = normalizeKenyaMobile(profile.phone || this.proposer.phone);
      if (profilePhone) {
        this.proposer = { ...this.proposer, phone: profilePhone };
        if (!this.mpesaPhone) this.mpesaPhone = profilePhone;
      }
    },
    formatMoney(n) {
      return Number(n || 0).toLocaleString('en-KE');
    },
    persistProgress() {
      const email = this.authenticatedUser?.email;
      savePaSaleProgress(email, this.effectiveQuoteId, {
        activeStep: this.activeStep,
        health: this.health,
        kin: this.kin,
        nationalId: this.nationalId,
        kraPin: this.kraPin,
        proposerName: this.proposerName,
        expectedFirstName: this.expectedFirstName,
        proposer: this.proposer,
        coverStartDate: this.coverStartDate,
        kraVerified: this.kraVerified,
        iprsUnavailable: this.iprsUnavailable,
      });
    },
    sanitizeKra(v) {
      return String(v || '').replace(/\s/g, '').toUpperCase().slice(0, 11);
    },
    clampCoverStartDate() {
      if (!this.coverStartDate) return;
      if (this.coverStartDate < this.coverStartMin) {
        this.coverStartDate = this.coverStartMin;
      } else if (this.coverStartDate > this.coverStartMax) {
        this.coverStartDate = this.coverStartMax;
      }
    },
    goBack() {
      const query = {};
      if (this.paConfig.channel === 'agent') {
        query.journey = this.$route.query.journey || 'quote';
      }
      this.$router.push({ name: this.paConfig.routes.planSelect, query });
    },
    startNewSale() {
      const email = this.authenticatedUser?.email;
      clearAllSaleData(email);
      this.showNewSaleConfirm = false;
      const query = { reset: 'true' };
      if (this.paConfig.channel === 'agent') {
        query.journey = this.$route.query.journey || 'quote';
      }
      this.$router.push({
        name: this.paConfig.routes.planSelect,
        query,
      });
    },
    async verifyIdentity() {
      const idCheck = validateNationalId(this.nationalId);
      if (!idCheck.valid) {
        this.$toast.error(idCheck.message);
        return;
      }
      if (!this.useCognitoIdentity && !this.expectedFirstName.trim()) {
        this.$toast.error('Enter the expected first name');
        return;
      }
      if (this.useCognitoIdentity && !this.cognitoIdReady) {
        this.$toast.error('Your account does not have a valid National ID on file');
        return;
      }
      if (this.useCognitoIdentity && !this.expectedFirstName.trim()) {
        this.applyCustomerCognitoProfile();
      }
      this.nationalId = idCheck.normalized;
      this.fetching = true;
      this.harvestMismatch = false;
      this.iprsUnavailable = false;
      this.idVerified = false;
      if (this.paConfig.trackAnalytics) {
        trackAgentInteraction(this, 'pa_identity_verify', { quoteId: this.effectiveQuoteId });
      }
      try {
        const res = await verifyPaIdentity(this.nationalId, this.channel);

        if (res.kraVerified && res.kraPin) {
          this.kraPin = this.sanitizeKra(res.kraPin);
          this.kraTaxpayerName = (res.kraTaxpayerName || '').trim();
          this.kraVerified = true;
        } else {
          this.kraVerified = false;
          this.kraTaxpayerName = '';
        }

        if (res.iprs) {
          const expected = this.expectedFirstName.trim().toUpperCase();
          const fetched = (res.iprs.firstName || '').toUpperCase();
          if (fetched && (fetched.includes(expected) || expected.includes(fetched))) {
            this.proposerName = res.iprs.fullName;
            this.gender = res.iprs.gender;
            this.dateOfBirth = res.iprs.dateOfBirth;
            this.occupation = res.iprs.occupation;
            if (res.iprs.occupation) {
              this.proposer = { ...this.proposer, occupation: res.iprs.occupation };
            }
            this.idVerified = true;
            this.$toast.success('Identity verified');
          } else {
            this.harvestMismatch = true;
          }
        } else {
          this.iprsUnavailable = true;
          this.$toast.info('IPRS unavailable — enter customer details manually');
        }
        if (!this.kraVerified && (this.idVerified || this.iprsUnavailable)) {
          this.$toast.info('KRA lookup unavailable — enter the PIN manually');
        }
      } catch (err) {
        this.iprsUnavailable = true;
        this.$toast.info('Could not reach IPRS — enter customer details manually');
      } finally {
        this.fetching = false;
      }
    },
    resetIdentity() {
      this.idVerified = false;
      this.iprsUnavailable = false;
      this.harvestMismatch = false;
      this.kraVerified = false;
      this.kraTaxpayerName = '';
      if (this.useCognitoIdentity) {
        this.applyCustomerCognitoProfile();
      }
    },
    async confirmIdAndNext() {
      if (!this.idCanProceed) return;
      if (!isPaCoverStartDateValid(this.coverStartDate)) {
        this.$toast.error('Select a cover start date within the allowed window (1 month back to 3 months ahead)');
        return;
      }
      if (!this.$refs.proposerForm?.validateAll()) {
        this.$toast.error('Please complete the student & contact details');
        return;
      }
      const id = validateNationalId(this.nationalId).normalized;
      const kra = validateKraPin(this.kraPin).normalized;
      this.nationalId = id;
      this.kraPin = kra;
      const name = (this.proposerName || '').trim();
      const payload = {
        nationalId: id,
        kraPin: kra,
        coverFrom: this.coverStartDate,
        ...this.proposer,
      };
      if (name) payload.proposerName = name;
      const res = await updatePaQuotation(this.effectiveQuoteId, payload);
      if (res.success) {
        this.quote = {
          ...this.quote,
          ...res.data,
          nationalId: id,
          kraPin: kra,
          proposerName: name || this.quote.proposerName,
          ...this.proposer,
        };
        savePaPendingQuote(this.authenticatedUser?.email, this.quote);
        this.activeStep = 1;
        this.$toast.success(this.paConfig.channel === 'customer' ? 'Your details were saved' : 'Customer details saved');
      }
    },
    goToPayment() {
      if (!isPaCoverStartDateValid(this.coverStartDate)) {
        this.$toast.error('Select a valid cover start date before continuing to payment');
        this.activeStep = 0;
        return;
      }
      if (!this.$refs.kinForm?.validateAll()) {
        this.$toast.error('Please complete next of kin details');
        return;
      }
      this.activeStep = 3;
    },
    async preparePaymentStep() {
      if (!validateNationalId(this.quote.nationalId).valid || !validateKraPin(this.quote.kraPin).valid) {
        this.$toast.error('National ID and KRA PIN are required before payment');
        this.activeStep = 0;
        return { ok: false };
      }
      if (!this.declarationsAccepted) {
        this.$toast.error('Please accept the terms to continue');
        return { ok: false };
      }
      await updatePaQuotation(this.effectiveQuoteId, {
        health: this.health,
        kin: this.kin,
        coverFrom: this.coverStartDate,
      });
      const initRes = await initPaPolicy(this.effectiveQuoteId);
      if (!initRes.success) {
        this.$toast.error(initRes.message || 'Could not prepare payment');
        return { ok: false };
      }
      return { ok: true, premium: this.quote.premium || initRes.data?.premium || 0 };
    },
    finishPaymentSuccess(email, message, { alreadyPaid = false } = {}) {
      const text = message || (alreadyPaid
        ? 'This quote is already paid.'
        : 'Payment received. Your policy will be issued shortly.');
      if (alreadyPaid) {
        this.$toast.info(text, 'Already paid');
      } else {
        this.$toast.success(text);
      }
      clearPaQuoteCache(email, this.effectiveQuoteId);
      invalidateAfterPayment(this.$store, this.paConfig.channel === 'agent' ? 'agent' : 'customer');
      this.$router.push({
        name: this.paConfig.routes.finalization,
        params: { quoteId: this.effectiveQuoteId },
      });
    },
    async verifyPaybillPayment() {
      this.paying = true;
      if (this.paConfig.trackAnalytics) {
        trackAgentInteraction(this, 'pa_payment_validate_paybill', {
          quoteId: this.effectiveQuoteId,
          reference: this.paymentReference,
        });
      }
      try {
        const email = this.authenticatedUser?.email;
        const prepared = await this.preparePaymentStep();
        if (!prepared.ok) {
          return;
        }
        const valRes = await confirmPaPaybillPayment({
          quoteId: this.effectiveQuoteId,
          accountNumber: this.paymentReference,
        });
        if (!valRes.success) {
          this.$toast.error(
            valRes.message || 'Payment not yet received. Complete Paybill payment and try again.',
          );
          return;
        }
        if (this.mockEnabled) {
          const issueRes = await issuePaPolicy(this.effectiveQuoteId);
          if (!issueRes.success) {
            this.$toast.error(issueRes.message || 'Issuance failed');
            return;
          }
          this.quote = { ...this.quote, ...issueRes.data };
        }
        this.finishPaymentSuccess(email, valRes.message, { alreadyPaid: valRes.alreadyPaid });
      } finally {
        this.paying = false;
      }
    },
    async pay(method) {
      if (method !== 'mpesa') return;
      this.paying = true;
      if (this.paConfig.trackAnalytics) {
        trackAgentInteraction(this, 'pa_payment_start', { quoteId: this.effectiveQuoteId, method });
      }
      try {
        const email = this.authenticatedUser?.email;
        const prepared = await this.preparePaymentStep();
        if (!prepared.ok) {
          return;
        }
        const stkRes = await requestPaStkPush({
          quoteId: this.effectiveQuoteId,
          payableAmount: prepared.premium,
          customerPhoneNumber: this.mpesaPhone,
          transactionReference: this.paymentReference,
          channel: this.channel,
          agentId: this.agentPaymentId,
          kraPin: this.kraPin,
        });
        if (!stkRes.success) {
          this.$toast.error(stkRes.message || 'Failed to send M-Pesa prompt');
          this.paybillFallback = true;
          this.activePayTab = 'paybill';
          return;
        }
        if (this.mockEnabled) {
          const issueRes = await issuePaPolicy(this.effectiveQuoteId);
          if (!issueRes.success) {
            this.$toast.error(issueRes.message || 'Issuance failed');
            return;
          }
          this.quote = { ...this.quote, ...issueRes.data };
          this.finishPaymentSuccess(email);
          return;
        }
        this.$toast.success('M-Pesa prompt sent. Complete payment on your phone.');
        this.pollPaPayment(stkRes.data.paymentId, email);
      } finally {
        if (this.mockEnabled) this.paying = false;
      }
    },
    pollPaPayment(paymentId, email) {
      pollPaPaymentStatus(paymentId, this.channel).then((res) => {
        if (!res.success) {
          this.paying = false;
          this.$toast.error(res.message || 'Could not verify payment');
          return;
        }
        const status = res.data?.paymentStatus;
        if (status === 'SUCCESSFUL') {
          this.paying = false;
          this.finishPaymentSuccess(email);
        } else if (status === 'FAILED' || status === 'CANCELLED') {
          this.paying = false;
          this.paybillFallback = true;
          this.activePayTab = 'paybill';
          this.$toast.error('Payment failed or was cancelled. You can pay via Paybill.');
        } else {
          this.pollTimer = setTimeout(() => this.pollPaPayment(paymentId, email), 10000);
        }
      }).catch(() => {
        this.paying = false;
        this.$toast.error('Could not verify payment status.');
      });
    },
  },
  watch: {
    authenticatedUser: {
      handler() {
        if (this.useCognitoIdentity) {
          this.applyCustomerCognitoProfile();
          this.autoVerifyCustomerIdentity();
        }
      },
    },
    activeStep() { this.persistProgress(); },
    health: { deep: true, handler() { this.persistProgress(); } },
    kin: { deep: true, handler() { this.persistProgress(); } },
    proposer: { deep: true, handler() { this.persistProgress(); } },
    coverStartDate() { this.persistProgress(); },
  },
  metaInfo: { title: 'Students PA — Sale' },
};
</script>

<style lang="scss" scoped>
@import '@/styles/agent-pa-form.scss';

.pa-page { max-width: 1200px; margin: 0 auto; padding: 20px 28px 32px; }
.pa-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
.pa-header-left { display: flex; align-items: flex-start; gap: 12px; }
.back-btn {
  width: 36px; height: 36px; border-radius: 50%; border: 1px solid #E2E8F0;
  background: white; display: flex; align-items: center; justify-content: center; cursor: pointer;
}
.btn-new-sale {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
  color: #1E4276;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
}
.btn-new-sale:hover { background: #EEF2FF; }
.breadcrumb { font-size: 0.75rem; color: #94A3B8; margin: 0; }
.title { font-size: 1.4rem; font-weight: 700; color: #1E4276; margin: 4px 0 0; }
.step-progress { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 20px; }
.step-item {
  display: flex; align-items: center; gap: 8px; padding: 8px 14px; border-radius: 20px;
  background: #F1F5F9; font-size: 0.75rem; font-weight: 600; color: #64748B;
}
.step-item.active { background: #E0F2FE; color: #0369A1; }
.step-item.done { background: #DCFCE7; color: #15803D; }
.step-dot {
  width: 22px; height: 22px; border-radius: 50%; background: white;
  display: inline-flex; align-items: center; justify-content: center; font-size: 0.7rem;
}
.quote-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 24px;
  align-items: start;
}
.section-card {
  background: white; border-radius: 12px; padding: 22px 24px; border: 1px solid #E8EDF2;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.section-header { display: flex; align-items: center; margin-bottom: 14px; }
.section-number {
  width: 26px; height: 26px; border-radius: 50%; background: #00AEEF; color: white;
  font-size: 0.75rem; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; margin-right: 10px;
}
.section-header h3 { margin: 0; font-size: 1.05rem; color: #1E4276; font-weight: 700; }
.step-hint { font-size: 0.82rem; color: #64748B; margin: 0 0 14px; line-height: 1.5; }
.verified-strip {
  display: flex; align-items: center; margin-top: 8px; padding: 10px 12px;
  background: #F0FDF4; border: 1px solid #BBF7D0; border-radius: 8px;
  font-size: 0.82rem; font-weight: 600; color: #15803D;
}
.input-label { display: block; font-size: 0.8rem; font-weight: 600; color: #334155; margin-bottom: 6px; }
.input-label .req { color: #DC2626; }
.field-hint { font-size: 0.7rem; color: #94A3B8; margin: 4px 0 0; }
.field-hint--warn { color: #B45309; }
.field-error { font-size: 0.72rem; color: #DC2626; margin-top: 4px; display: block; }
.verify-row { display: flex; align-items: flex-start; gap: 10px; }
.verify-btn {
  flex-shrink: 0; height: 40px; padding: 0 16px; border-radius: 8px; border: none;
  background: #2BABE2; color: white; font-weight: 700; font-size: 0.82rem; cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px;
}
.verify-btn:hover:not(:disabled) { background: #1e94c8; }
.verify-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.verify-btn--block { width: 100%; justify-content: center; height: 44px; }
.account-id-card {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 14px 16px;
}
.account-id-label {
  margin: 0 0 10px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748B;
}
.account-id-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 6px 0;
}
.account-id-row + .account-id-row { border-top: 1px solid #E2E8F0; }
.account-id-key { font-size: 0.8rem; color: #64748B; font-weight: 600; }
.account-id-val { font-size: 0.9rem; color: #1E4276; font-weight: 700; text-align: right; }
.verify-auto-status {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
}
.iprs-card {
  background: #F0FDF4; border: 1px solid #BBF7D0; border-radius: 10px; padding: 14px 16px;
}
.reset-link {
  margin-left: auto; background: none; border: none; color: #2563EB; font-weight: 600;
  font-size: 0.75rem; cursor: pointer; text-decoration: underline;
}
.iprs-card-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}
.verified-details {
  background: white;
  border: 1px solid #BBF7D0;
  border-radius: 8px;
  overflow: hidden;
}
.verified-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  padding: 10px 14px;
}
.verified-detail-row + .verified-detail-row {
  border-top: 1px solid #DCFCE7;
}
.verified-detail-key {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  flex-shrink: 0;
}
.verified-detail-val {
  font-size: 0.88rem;
  font-weight: 700;
  color: #14532D;
  text-align: right;
  word-break: break-word;
}
.alert-error {
  background: #FEF2F2; border: 1px solid #FECACA; border-radius: 8px; padding: 10px 12px;
  font-size: 0.8rem; color: #B91C1C;
}
.alert-info {
  background: #EFF6FF; border: 1px solid #BFDBFE; border-radius: 8px; padding: 10px 12px;
  font-size: 0.8rem; color: #1D4ED8;
}
.proposer-extra { border-top: 1px dashed #E2E8F0; padding-top: 14px; }
.subsection-title {
  font-size: 0.78rem; font-weight: 700; color: #1E4276; text-transform: uppercase;
  letter-spacing: 0.02em; margin: 0 0 10px;
}
.cover-start-block { margin-bottom: 0; }
.cover-start-input { max-width: 280px; }
.section-divider {
  border: none;
  border-top: 1px solid #E2E8F0;
  margin: 20px 0;
}
.step-hint--subsection {
  margin-top: 0;
  margin-bottom: 14px;
}
.actions-row { display: flex; gap: 12px; justify-content: flex-end; margin-top: 20px; }
.btn-outline, .btn-primary {
  padding: 11px 22px; border-radius: 10px; font-weight: 700; cursor: pointer; border: none;
  display: inline-flex; align-items: center;
}
.btn-outline { border: 1px solid #CBD5E1; background: white; color: #1E4276; }
.btn-primary { background: #2BABE2; color: white; }
.btn-primary:hover:not(:disabled) { background: #1e94c8; }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.review-block {
  background: linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%);
  border: 1px solid #E2E8F0;
  padding: 18px;
  border-radius: 12px;
}
.review-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}
.review-name {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 800;
  color: #0F172A;
  letter-spacing: 0.01em;
}
.review-status {
  font-size: 0.68rem;
  font-weight: 700;
  color: #166534;
  background: #DCFCE7;
  border: 1px solid #BBF7D0;
  border-radius: 999px;
  padding: 4px 10px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.review-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
.review-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 10px 12px;
}
.review-key {
  font-size: 0.78rem;
  color: #64748B;
  font-weight: 600;
}
.review-val {
  font-size: 0.9rem;
  color: #0F172A;
  font-weight: 700;
  text-align: right;
}
.premium-pill {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 12px;
  background: linear-gradient(135deg, #1E4276, #2BABE2);
  color: white;
  padding: 12px 14px;
}
.premium-key {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.9;
}
.premium-val {
  font-size: 1.15rem;
  font-weight: 800;
}
.review-check {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 8px 10px;
}
.decl-text { font-size: 0.82rem; color: #334155; font-weight: 500; }
.pay-btn {
  width: 100%; display: flex; align-items: center; justify-content: center;
  background: #16A34A; color: white; font-weight: 700; padding: 14px; border-radius: 10px; border: none; cursor: pointer;
}
.pay-btn:hover:not(:disabled) { background: #15803D; }
.pay-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.pay-btn--block {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pay-decl-check {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 4px 10px;
}
.demo-note { font-size: 0.72rem; color: #94A3B8; margin-top: 12px; text-align: center; }
.quote-sidebar { position: sticky; top: 20px; }
.quote-layout--checkout {
  grid-template-columns: 1fr !important;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
}

/* Payment step (aligned with motor agent sale) */
.section-sub-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 0 0 10px;
}
.pay-summary-card {
  background: white;
  border: 1.5px solid #E8EDF2;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(30, 66, 118, 0.06);
}
.pay-summary-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 13px 16px;
  background: linear-gradient(90deg, #1E4276 0%, #2A5A9E 100%);
  font-size: 0.82rem;
  font-weight: 700;
  color: white;
}
.pay-summary-head-icon {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}
.pay-summary-section { padding: 12px 16px; border-bottom: 1px solid #F1F5F9; }
.pay-sec-label {
  font-size: 0.68rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 8px;
}
.pay-sum-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 5px 0;
}
.pay-sum-label { font-size: 0.74rem; font-weight: 600; color: #64748b; }
.pay-sum-value { font-size: 0.78rem; color: #1E4276; font-weight: 600; text-align: right; }
.pay-premium-footer { padding: 12px 16px; background: #F8FBFF; }
.pay-prem-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1E4276;
}
.pay-prem-total-val { font-size: 1.15rem; font-weight: 800; color: #00AEEF; }
.pay-method-tabs { display: flex; gap: 8px; }
.pay-method-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1.5px solid #E8EDF2;
  border-radius: 10px;
  background: white;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 600;
  color: #64748b;
  transition: all 0.15s;
}
.pay-method-tab:hover { border-color: #BAE6FD; color: #1E4276; }
.pay-tab-active {
  border-color: #1E4276 !important;
  background: #F0F4FA !important;
  color: #1E4276 !important;
  box-shadow: 0 0 0 1px #1E4276;
}
.pay-tab-mpesa-logo { height: 22px; object-fit: contain; }
.pay-panel { animation: payFadeIn 0.2s ease-out; }
@keyframes payFadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
.mpesa-panel-card {
  background: #F8FBFF;
  border: 1.5px solid #DEE9F5;
  border-radius: 12px;
  padding: 16px;
}
.mpesa-panel-header { display: flex; align-items: center; gap: 12px; }
.mpesa-panel-logo { height: 36px; object-fit: contain; }
.mpesa-panel-title { font-size: 0.88rem; font-weight: 700; color: #1E4276; margin: 0 0 2px; }
.mpesa-panel-sub { font-size: 0.72rem; color: #64748b; margin: 0; }
.pay-ref-chip {
  display: inline-flex;
  align-items: center;
  background: white;
  border: 1px solid #E8EDF2;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 0.72rem;
}
.pay-ref-label { color: #94a3b8; margin-right: 5px; font-weight: 600; }
.pay-ref-code {
  font-family: ui-monospace, monospace;
  font-weight: 700;
  color: #1E4276;
  letter-spacing: 0.05em;
}
.pay-stk-hint {
  margin-top: 14px;
  padding: 12px 14px;
  font-size: 0.8rem;
  color: #64748b;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  line-height: 1.55;
  background: #F8FAFC;
  border: 1px solid #E8EDF2;
  border-radius: 10px;
}
.pay-stk-hint-icon { flex-shrink: 0; margin-top: 2px; }
.pay-stk-hint-text { flex: 1; min-width: 0; }
.pay-stk-phone {
  white-space: nowrap;
  color: #1E4276;
  font-weight: 700;
}
.btn-pay {
  margin-top: 4px;
  height: 48px;
  background: linear-gradient(135deg, #1E4276, #2A5A9E);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(30, 66, 118, 0.3);
  transition: all 0.2s;
}
.btn-pay:hover:not(:disabled) { transform: translateY(-1px); }
.btn-pay:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-amber {
  height: 48px;
  background: #FFC107;
  color: #1E4276;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.35);
}
.btn-amber--block { width: 100%; margin-top: 16px; }
.btn-amber:hover:not(:disabled) { filter: brightness(0.97); }
.btn-amber:disabled { opacity: 0.5; cursor: not-allowed; }
.paybill-fallback-alert {
  display: flex;
  align-items: center;
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #92400e;
}
.paybill-panel-card {
  background: white;
  border: 1.5px solid #E8EDF2;
  border-radius: 12px;
  overflow: hidden;
}
.paybill-panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(90deg, #1E4276, #2A5A9E);
}
.paybill-panel-title { font-size: 0.88rem; font-weight: 700; color: white; margin: 0 0 2px; }
.paybill-panel-sub { font-size: 0.72rem; color: rgba(255, 255, 255, 0.75); margin: 0; }
.paybill-ussd-steps { background: #F8FBFF; padding: 12px 16px; }
.paybill-ussd-title {
  font-size: 0.68rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 0 0 8px;
  display: flex;
  align-items: center;
}
.paybill-ussd-list { display: flex; flex-direction: column; gap: 5px; }
.paybill-ussd-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.74rem;
  color: #475569;
  line-height: 1.5;
}
.paybill-ussd-num {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #E8EDF2;
  font-size: 0.62rem;
  font-weight: 800;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.new-sale-dialog {
  background: white;
  border-radius: 14px;
  box-shadow: 0 20px 40px rgba(2, 6, 23, 0.25);
  overflow: hidden;
}
.nsd-header {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 16px 16px 10px;
  border-bottom: 1px solid #E2E8F0;
}
.nsd-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f59e0b, #ef4444);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.nsd-title { margin: 0; font-size: 1rem; font-weight: 700; color: #0F172A; }
.nsd-sub { margin: 2px 0 0; font-size: 0.8rem; color: #64748B; }
.nsd-body { padding: 12px 16px; }
.nsd-desc { margin: 0; font-size: 0.82rem; color: #334155; line-height: 1.5; }
.nsd-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 16px 16px;
}
.nsd-btn-cancel, .nsd-btn-confirm {
  border: none;
  border-radius: 9px;
  padding: 10px 14px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
}
.nsd-btn-cancel { background: #F1F5F9; color: #334155; }
.nsd-btn-cancel:hover { background: #E2E8F0; }
.nsd-btn-confirm {
  background: linear-gradient(135deg, #2BABE2, #1E4276);
  color: white;
  display: inline-flex;
  align-items: center;
}
.nsd-btn-confirm:hover { filter: brightness(0.95); }

@media (max-width: 960px) {
  .quote-layout { grid-template-columns: 1fr; }
  .quote-sidebar { position: static; }
  .pa-page { padding: 16px 16px 28px; }
  .section-card { padding: 16px; }
  .cover-start-input { max-width: none; }
}

@media (max-width: 600px) {
  .pa-page { padding: 12px 12px 88px; }
  .pa-header {
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  }
  .pa-header-left { min-width: 0; flex: 1 1 auto; }
  .btn-new-sale {
    flex-shrink: 0;
    padding: 7px 10px;
    font-size: 0.72rem;
  }
  .title { font-size: 1.15rem; line-height: 1.3; }
  .breadcrumb { font-size: 0.7rem; }

  .step-progress {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 6px;
    margin-bottom: 14px;
  }
  .step-item {
    flex-direction: column;
    justify-content: center;
    gap: 4px;
    padding: 8px 4px;
    border-radius: 10px;
    text-align: center;
    min-width: 0;
  }
  .step-label {
    font-size: 0.58rem;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
  .step-dot {
    width: 20px;
    height: 20px;
    font-size: 0.65rem;
  }

  .verify-row {
    flex-direction: column;
    align-items: stretch;
  }
  .verify-btn {
    width: 100%;
    justify-content: center;
    height: 44px;
  }

  .actions-row {
    flex-direction: column-reverse;
    gap: 10px;
  }
  .actions-row .btn-outline,
  .actions-row .btn-primary {
    width: 100%;
    justify-content: center;
    min-height: 46px;
  }

  .review-head {
    flex-direction: column;
    align-items: flex-start;
  }
  .account-id-row,
  .verified-detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  .account-id-val,
  .verified-detail-val {
    text-align: left;
  }

  .nsd-footer {
    flex-direction: column-reverse;
    gap: 8px;
  }
  .nsd-btn-cancel,
  .nsd-btn-confirm {
    width: 100%;
    justify-content: center;
  }

  .pay-sum-row {
    flex-wrap: wrap;
    align-items: flex-start;
  }
  .pay-sum-value {
    text-align: left;
    word-break: break-word;
  }
}
</style>
